import { z } from "zod";
import {
  SharedApiError,
  normalizeApiBaseUrl,
  parseErrorResponse,
  type FetchLike,
} from "./shared";

export const uploadSuccessSchema = z.object({
  id: z.string(),
  filename: z.string(),
  file_type: z.string(),
  file_size: z.number(),
  file_category: z.string(),
  storage_key: z.string(),
  storage_url: z.string(),
  thumbnail_url: z.string().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
});

export type UploadSuccess = z.infer<typeof uploadSuccessSchema>;

export function uploadPath(): string {
  return "/api/upload";
}

export async function uploadRequest({
  apiBaseUrl,
  accessToken,
  formData,
  fetchImpl,
}: {
  apiBaseUrl: string;
  accessToken: string;
  formData: FormData;
  fetchImpl?: FetchLike;
}): Promise<UploadSuccess> {
  const fetchFn = fetchImpl ?? fetch;
  const response = await fetchFn(
    `${normalizeApiBaseUrl(apiBaseUrl)}${uploadPath()}`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: formData,
    },
  );

  if (!response.ok) {
    throw await parseErrorResponse(response, "Upload failed");
  }

  const payload = await response.json().catch(() => null);
  const parsed = uploadSuccessSchema.safeParse(payload);
  if (!parsed.success) {
    throw new SharedApiError("Invalid upload response", 500, {
      issues: parsed.error.issues,
    });
  }

  return parsed.data;
}
